import React, { useEffect, useState } from "react";
import "./PresetPage.scss";
import { Button, Chip, Container, Grid, ButtonProps } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useHistory } from "react-router-dom";
import RoutesName from "routes/routesName";
import { styled } from "@mui/material/styles";
import SetupCard from "ui/components/preset/SetupCard";
import { useCmrAuth } from "state/CMRAuth";
import CategoryCard from "ui/components/category/CategoryCard";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import Header from "../../components/AppHeader";
import Footer from "../../components/AppFooter";

const ColorButton = styled(Button)<ButtonProps>(() => ({
    boxShadow: "none",
    textTransform: "none",
    fontSize: 18,
    fontWeight: "bold",
    border: "1px solid",
    color: "#fff",
    borderRadius: "50px",
    lineHeight: 1.5,
    backgroundColor: "#E6117B",
    borderColor: "#E6117B",
    fontFamily: [
        "Open Sans",
        "-apple-system",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
    ].join(","),
    "&:hover": {
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
        boxShadow: "none",
        color: "#fff",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
    },
    "&:focus": {
        boxShadow: "0 0 0 0.2rem rgba(230, 17,123,.2)",
    },
}));

const OutlineButton = styled(Button)<ButtonProps>(() => ({
    boxShadow: "none",
    textTransform: "none",
    fontSize: 18,
    fontWeight: "bold",
    border: "1px solid",
    color: "#E6117B",
    borderRadius: "50px",
    lineHeight: 1.5,
    backgroundColor: "#fff",
    borderColor: "#E6117B",
    fontFamily: [
        "Open Sans",
        "-apple-system",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
    ].join(","),
    "&:hover": {
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
        boxShadow: "none",
        color: "#fff",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
    },
    "&:focus": {
        boxShadow: "0 0 0 0.2rem rgba(230, 17,123,.2)",
    },
}));

type Scene = {
    id: string;
    user: string;
    sceneName: string;
    data: string;
    thumbnail: string | null;
    global: boolean;
    displayName: string;
};

const PresetPage = (): JSX.Element => {
    const [scenes, setScenes] = useState<Scene[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [grandTotal, setGrandTotal] = useState<number>(0);
    const [isLoadingCategories, setLoadingCategories] = useState<boolean>(true);
    const [isLoadingScenes, setLoadingScenes] = useState<boolean>(true);

    document.body.classList.remove("newPage");

    const handleClick = (type: string): void => {
        if (type === "new") {
            router.push({ pathname: RoutesName.newSetup });
        }
        if (type === "import") {
            router.push({ pathname: RoutesName.import });
        }
    };

    const refreshScenes = async () => {
        await Promise.all([getCustomScenes(), getCategories()]);
    };

    const cmrAuth = useCmrAuth();
    const router = useHistory();

    const getCustomScenes = async () => {
        try {
            const token = await cmrAuth.getAccessToken();

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/scenes`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const scenes = await res.json();
            setScenes(scenes);
        } catch (err) {
            setScenes([]);
        } finally {
            setLoadingScenes(false);
        }
    };

    const getCategories = async () => {
        try {
            const token = await cmrAuth.getAccessToken();

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await res.json();
            setCategories(data?.categories ?? []);
            setGrandTotal(data?.grandTotal ?? 0);
        } catch (err) {
            setCategories([]);
            setGrandTotal(0);
        } finally {
            setLoadingCategories(false);
        }
    };

    useEffect(() => {
        getCustomScenes();
        getCategories();
    }, []);

    const isLoading = isLoadingScenes || isLoadingCategories;

    return (
        <div className="fullWidthHeight presetPage noPadding">
            <Header />

            <Container maxWidth={"lg"}>
                <section className="presetBody">
                    <Grid container>
                        <Grid item xs={12} className="heading">
                            <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>My Setups</h3>
                        </Grid>
                    </Grid>

                    {isLoadingScenes ? (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <div className="empty-setups">
                                    <h4>Loading your setups...</h4>
                                    <p>Please wait while your setups are loaded.</p>
                                </div>
                            </Grid>
                        </Grid>
                    ) : scenes.length > 0 ? (
                        <Grid container spacing={3}>
                            {scenes.map((scene) => (
                                <SetupCard key={scene.id} scene={scene} onDeleted={refreshScenes} />
                            ))}
                        </Grid>
                    ) : (
                        <div className="empty-setups">
                            <h4>You have not created any setups.</h4>
                            <p>Create a new setup or import an existing one to get started.</p>
                            <div>
                                <ColorButton
                                    onClick={() => handleClick("new")}
                                    variant="contained"
                                    startIcon={<AddCircleOutlineOutlinedIcon className="icon" />}
                                >
                                    New Setup
                                </ColorButton>
                                <OutlineButton
                                    onClick={() => handleClick("import")}
                                    variant="contained"
                                    startIcon={<CloudUploadOutlinedIcon className="icon" />}
                                >
                                    Import Setup
                                </OutlineButton>
                            </div>
                        </div>
                    )}
                </section>

                <section className="sharedSetups">
                    <Grid container>
                        <Grid item xs={12} className="heading">
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <h3 style={{ marginBottom: "1rem" }}>Preset Setups</h3>
                                {!isLoadingCategories ? (
                                    <Chip label={`Total Setups ${grandTotal}`} color="primary" />
                                ) : (
                                    <Chip color="primary" />
                                )}
                            </div>
                            <p style={{ marginBottom: "1rem" }}>
                                Setups are organised into categories. Browse the category tree to
                                find and load a setup.
                            </p>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                        {categories.map((category) => (
                            <CategoryCard key={category.id} category={category} />
                        ))}
                    </Grid>
                </section>
            </Container>
            <Footer />
        </div>
    );
};
export default PresetPage;
